import axios, { AxiosRequestConfig } from 'axios'
import { CURRENT_CONFIG } from './config'
import { message } from 'ant-design-vue'
import router from '/@/router'
import { ELocalStorageKey, ERouterName, EUserType } from '/@/types/enums'
import JSONbig from 'json-bigint'// 解决精度问题
export * from './type'

const REQUEST_ID = 'X-Request-Id'
const DEFAULT_ERROR_MESSAGE_COOLDOWN = 3000
const errorMessageCooldownMap = new Map<string, number>()
const WEB_ENTRY_ROUTE = `/${ERouterName.PROJECT}`
const PILOT_ENTRY_ROUTE = `/${ERouterName.PILOT}`
let isUnauthorizedRedirecting = false
const AUTH_STORAGE_KEYS = [
  ELocalStorageKey.Token,
  ELocalStorageKey.WorkspaceId,
  ELocalStorageKey.Username,
  ELocalStorageKey.UserId,
  ELocalStorageKey.Flag,
  ELocalStorageKey.LargeScreen,
  ELocalStorageKey.RegionCode,
  ELocalStorageKey.RoleId,
  ELocalStorageKey.RoleKey,
  ELocalStorageKey.MenuList,
  'perms',
  'name'
]

interface RequestConfig extends AxiosRequestConfig {
  skipGlobalErrorMessage?: boolean
  errorMessageKey?: string
  errorMessageCooldownMs?: number
}

function shouldShowErrorMessage (key: string, cooldownMs = DEFAULT_ERROR_MESSAGE_COOLDOWN) {
  const now = Date.now()
  const lastShownAt = errorMessageCooldownMap.get(key) || 0
  if (now - lastShownAt < cooldownMs) {
    return false
  }
  errorMessageCooldownMap.set(key, now)
  return true
}

function showRequestErrorMessage (content: string, config?: RequestConfig, fallbackKey?: string) {
  if (config?.skipGlobalErrorMessage) {
    return
  }
  const messageKey = config?.errorMessageKey || fallbackKey || content
  const cooldownMs = config?.errorMessageCooldownMs || DEFAULT_ERROR_MESSAGE_COOLDOWN
  if (!shouldShowErrorMessage(messageKey, cooldownMs)) {
    return
  }
  message.error(content)
}

function getAuthToken () {
  return localStorage.getItem(ELocalStorageKey.Token)
}

function getPilotPreviewDevice () {
  return {
    sn: 'DRONE-PREVIEW-001',
    status: true,
    online_status: true,
    nickname: '预览飞行器',
    device_name: 'Matrice 30 预览机',
    device_callsign: '预览飞行器',
    user_id: 'dev-preview-user',
    user_callsign: '飞手预览账号',
    bound_status: false,
    model: 'Matrice 30 预览机',
    gateway_sn: 'RC-PREVIEW-001',
    domain: 0
  }
}

function createPilotPreviewResponse (config: RequestConfig, data: any) {
  return Promise.resolve({
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: {}
  })
}

function installPilotPreviewAdapter (config: RequestConfig) {
  if (!shouldUsePilotDevPreviewMock()) {
    return
  }
  const url = String(config.url || '')
  if (url.endsWith('/workspaces/current')) {
    config.adapter = () => createPilotPreviewResponse(config, {
      code: 0,
      message: 'success',
      data: {
        platform_name: '低空巡查管理平台',
        workspace_name: '低空巡查默认工作空间',
        workspace_id: 'dev-preview-workspace',
        workspace_desc: '本地预览工作空间'
      }
    })
  } else if (url.endsWith('/token/refresh') || url.endsWith('/login')) {
    config.adapter = () => createPilotPreviewResponse(config, {
      code: 0,
      message: 'success',
      data: {
        access_token: 'dev-preview-token',
        workspace_id: 'dev-preview-workspace',
        user_id: 'dev-preview-user',
        username: '飞手预览账号'
      }
    })
  } else if (url.endsWith('/users/current')) {
    config.adapter = () => createPilotPreviewResponse(config, {
      code: 0,
      message: 'success',
      data: {
        username: '飞手预览账号',
        user_id: 'dev-preview-user',
        workspace_id: 'dev-preview-workspace',
        mqtt_addr: 'ws://127.0.0.1:1884/mqtt',
        mqtt_username: 'preview',
        mqtt_password: 'preview'
      }
    })
  } else if (url.includes('/devices/') && url.endsWith('/binding')) {
    config.adapter = () => createPilotPreviewResponse(config, {
      code: 0,
      message: 'success',
      data: true
    })
  } else if (url.includes('/devices/') && !url.endsWith('/binding')) {
    config.adapter = () => createPilotPreviewResponse(config, {
      code: 0,
      message: 'success',
      data: getPilotPreviewDevice()
    })
  }
}

function shouldUsePilotDevPreviewMock () {
  if (!import.meta.env.DEV || !window.location.pathname.startsWith('/pilot-')) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  if (params.get('pilotReal') === '1') {
    return false
  }
  if (params.get('pilotMock') === '1') {
    return true
  }
  return !(window as any).djiBridge
}

function clearAuthStorage () {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
}

function redirectToEntryRoute (flag: number) {
  const targetPath = flag === EUserType.Pilot ? PILOT_ENTRY_ROUTE : WEB_ENTRY_ROUTE
  // A router navigation to the current login route is ignored by Vue Router,
  // leaving the expired page visible until the browser is refreshed.
  window.location.replace(router.resolve(targetPath).href)
}

function handleUnauthorizedRedirect () {
  if (isUnauthorizedRedirecting) {
    return
  }
  isUnauthorizedRedirecting = true
  const flag: number = Number(localStorage.getItem(ELocalStorageKey.Flag))
  clearAuthStorage()
  message.warning('登录已过期，请重新登录')
  setTimeout(() => {
    isUnauthorizedRedirecting = false
  }, 5000)
  redirectToEntryRoute(flag)
}

axios.defaults.transformResponse = [function (data:any) {
  if (!data) {
    return data
  }
  const json = JSONbig({
    storeAsString: true,
  })
  try {
    return json.parse(data)
  } catch (err) {
    return data
  }
}]
const instance = axios.create({
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  baseURL: CURRENT_CONFIG.baseURL,
  // timeout: 12000,
})

instance.interceptors.request.use(
  config => {
    installPilotPreviewAdapter(config as RequestConfig)
    config.headers[ELocalStorageKey.Token] = getAuthToken()
    return config
  },
  error => {
    return Promise.reject(error)
  },
)

function isLoginRequest (url: string) {
  return url.includes('/vsLogin') || url.includes('/login')
}

instance.interceptors.response.use(
  response => {
    const config = response.config as RequestConfig
    const businessCode = Number(response.data?.code)
    if (response.data?.code !== undefined && businessCode !== 0 && businessCode !== 200) {
      if (Number(response.data.code) === 401 && !isLoginRequest(String(response.config.url))) {
        if (shouldUsePilotDevPreviewMock()) {
          return Promise.reject(response.data)
        }
        handleUnauthorizedRedirect()
      } else {
        showRequestErrorMessage(response.data.message, config, `biz:${response.data.code}:${response.data.message}`)
      }
      return Promise.reject(response.data)
    }
    return response
  },
  err => {
    const config = err?.config as RequestConfig | undefined
    const requestId = err?.config?.headers && err?.config?.headers[REQUEST_ID]
    if (requestId) {
      console.info(REQUEST_ID, '：', requestId)
    }
    console.info('url: ', err?.config?.url, `【${err?.config?.method}】 \n>>>> err: `, err)

    let description = '-'
    if (err.response?.data && err.response.data.message) {
      description = err.response.data.message
    }
    if (err.response?.data && err.response.data.result) {
      description = err.response.data.result.message
    }
    if ((err.response?.status === 401 || Number(err.response?.data?.code) === 401) && !isLoginRequest(String(err?.config?.url))) {
      if (shouldUsePilotDevPreviewMock()) {
        return Promise.reject(err)
      }
      handleUnauthorizedRedirect()
      return Promise.reject(err)
    }
    if (!err.response || !err.response.status) {
      if (shouldUsePilotDevPreviewMock()) {
        return Promise.reject(err)
      }
      showRequestErrorMessage(description === '-' ? '网络错误或请求超时，请检查网络或服务状态' : description, config, 'network-timeout')
      return Promise.reject(err)
    }
    if (err.response?.status !== 200) {
      if (err.response?.status === 401 && !isLoginRequest(String(err?.config?.url))) {
        if (shouldUsePilotDevPreviewMock()) {
          return Promise.reject(err)
        }
        handleUnauthorizedRedirect()
      } else {
        showRequestErrorMessage(`ERROR_CODE: ${err.response?.status}`, config, `http:${err.response?.status}`)
      }
    }
    return Promise.reject(err)
  },
)

export default instance
